import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RoadHorizon } from "@phosphor-icons/react";
import { fetchTollTags, fetchTrucks } from "@/lib/api";
import { TagsManager } from "@/components/telepeage/TagsManager";
import { TrucksView } from "@/components/telepeage/TrucksView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg } from "@/components/telepeage/utils";

const Stat = ({ label, value, tone, testId }) => (
  <div className={`border-2 px-4 py-3 ${tone}`} data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
    <div className="mono text-3xl font-bold">{value}</div>
  </div>
);

export default function TelepeagesPage() {
  const [tags, setTags] = useState([]);
  const [trucks, setTrucks] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [t, tr] = await Promise.all([fetchTollTags(), fetchTrucks()]);
      const used = new Set(t.map((x) => x.assigned_truck_id));
      setTags(t);
      setTrucks(
        tr.filter((x) => x.site === "DLP4" && (x.status !== "rendu" || used.has(x.id)))
          .sort((a, b) => a.license_plate.localeCompare(b.license_plate, "fr"))
      );
    } catch (e) {
      toast.error(errMsg(e, "Impossible de charger les télépéages"));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const assigned = tags.filter((t) => t.assigned_truck_id).length;
  const equipped = new Set(tags.map((t) => t.assigned_truck_id).filter(Boolean));
  const withoutTag = trucks.filter((t) => t.status !== "rendu" && !equipped.has(t.id)).length;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-8" data-testid="telepeages-page">
      <div className="mb-8">
        <div className="label-tag mb-1">NY TRANS · DLP4</div>
        <h1 className="text-4xl sm:text-5xl serif-display flex items-center gap-3">
          <RoadHorizon size={40} weight="duotone" className="text-[var(--ny-gold-deep)]" /> Télépéages
        </h1>
        <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">
          Chaque badge reste attribué à son camion · modifiable à tout moment
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-8">
        <Stat label="Badges" value={tags.length} tone="border-zinc-900 bg-white" testId="toll-stat-total" />
        <Stat label="Attribués" value={assigned} tone="border-emerald-600 bg-emerald-50 text-emerald-800" testId="toll-stat-assigned" />
        <Stat label="Camions sans badge" value={withoutTag} tone={withoutTag ? "border-amber-500 bg-amber-50 text-amber-800" : "border-zinc-300 bg-white text-zinc-500"} testId="toll-stat-without" />
      </div>
      <Tabs defaultValue="trucks">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-900 w-full justify-start p-0 h-auto mb-6">
          <TabsTrigger value="trucks" className="rounded-none px-5 py-2 uppercase tracking-widest text-xs font-bold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="toll-tab-trucks">Par camion ({trucks.filter((t) => t.status !== "rendu").length})</TabsTrigger>
          <TabsTrigger value="tags" className="rounded-none px-5 py-2 uppercase tracking-widest text-xs font-bold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="toll-tab-tags">Par badge ({tags.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="trucks">
          <TrucksView trucks={trucks} tags={tags} reload={reload} />
        </TabsContent>
        <TabsContent value="tags">
          <TagsManager tags={tags} trucks={trucks} reload={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
